import type { AmsFundingProgram, FundingProgramState } from "../../booking-season/bookingSeasonApi"
import type { FundingSwitchSaveRefusal, SaveFundingSwitchRequest } from "../fundingSwitchesApi"

/** Longest message the server accepts, after trimming (BookingSeasonDefaults.MessageMaxLength). */
export const MESSAGE_MAX_LENGTH = 2000

/** The program's name as the website writes it (FundingSwitchDefaults.DisplayName). */
export function programLabel(program: AmsFundingProgram): string {
  switch (program) {
    case "Liheap":
      return "LIHEAP"
    case "PseHelpGas":
      return "PSE HELP (gas)"
    case "PseHelpElectric":
      return "PSE HELP (electric)"
    case "Sheap":
      return "SHEAP"
  }
}

/** What the public is told about a program that is off when no message was saved (FundingSwitchDefaults.DefaultMessage). */
export function defaultMessage(program: AmsFundingProgram): string {
  return `${programLabel(program)} is unavailable at this time.`
}

/** One row of the editor: the switch and the message as being edited. */
export interface SwitchRowState {
  isEnabled: boolean
  /** Blank means the default message. */
  message: string
}

/**
 * The row as the server holds it. The server answers with the default message when none was saved,
 * so a message equal to the default shows as blank: saving blank keeps the default.
 */
export function rowFromState(state: FundingProgramState): SwitchRowState {
  return {
    isEnabled: state.isEnabled,
    message: state.message === defaultMessage(state.program) ? "" : state.message,
  }
}

export function sameRow(a: SwitchRowState, b: SwitchRowState): boolean {
  return a.isEnabled === b.isEnabled && a.message.trim() === b.message.trim()
}

/** The reason the row cannot be saved, or null when it can. */
export function validateRow(row: SwitchRowState): string | null {
  return row.message.trim().length > MESSAGE_MAX_LENGTH
    ? `Message must be ${MESSAGE_MAX_LENGTH} characters or fewer.`
    : null
}

/** The PUT body for the row, or null while it does not validate. */
export function buildSaveRequest(program: AmsFundingProgram, row: SwitchRowState): SaveFundingSwitchRequest | null {
  if (validateRow(row)) return null
  const message = row.message.trim()
  return { program, isEnabled: row.isEnabled, message: message === "" ? null : message }
}

export function isSaveRefusal(data: unknown): data is FundingSwitchSaveRefusal {
  return (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as { errors?: unknown }).errors) &&
    (data as { errors: unknown[] }).errors.every((e) => typeof e === "string")
  )
}
